import React from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Button, ButtonText } from '@/components/ui/button'
import ChevronDownIcon from '@heroicons/react/16/solid/ChevronDownIcon'
import {
  ArrowDownRightIcon,
  ArrowPathRoundedSquareIcon,
  ArrowUpRightIcon,
  BeakerIcon,
  PlusCircleIcon,
  QueueListIcon,
  Squares2X2Icon,
  TableCellsIcon,
} from '@heroicons/react/16/solid'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import useGetModelsList from '@/services/api/model/useGetModelsList'
import useGetTodoCount from '@/services/api/to-do/useGetTodoCount'
import {
  FeatureGate,
  useFeaturesLoaded,
  useHasFeature,
} from '@/components/feature-gate'
import HorizonMenu from '@/layouts/HorizonMenu'
import SpacesMenu from '@/layouts/SpacesMenu'
import AccountMenu from '@/layouts/AccountMenu'
import TableControlRow from '@/layouts/TableControlRow'
import DashboardControlRow from '@/pages/dashboard/DashboardControlRow'
import PaymentIssueBanner from '@/components/billing/PaymentIssueBanner'

export default function GlobalBar() {
  const navigate = useNavigate()
  const location = useLocation()
  const isDashboardRoute = location.pathname === '/'

  const hasTodoFeature = useHasFeature('to-do')
  const hasModelsFeature = useHasFeature('models')
  const featuresLoaded = useFeaturesLoaded()
  const { data: todoCount } = useGetTodoCount({
    enabled: featuresLoaded && hasTodoFeature,
  })
  const { data: modelsList } = useGetModelsList({
    enabled: featuresLoaded && hasModelsFeature,
  })

  const enabledModelsCount = (modelsList ?? []).filter(
    (model) => model.is_enabled,
  ).length

  return (
    <div className="min-w-0">
      <PaymentIssueBanner />
      {/* ROW 1 — identity, spaces, view switch, global actions */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-primary bg-white px-3 py-2">
        <div className="flex min-w-0 items-center gap-3">
          <AccountMenu />
          <SpacesMenu />
          <div className="hidden h-6 w-px bg-[hsl(var(--border-primary))] tablet:block" />
          <div className="flex items-center gap-1 rounded-[9px] border border-primary bg-layout-secondary p-[3px]">
            <button
              type="button"
              onClick={() => navigate('/')}
              className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 text-sm font-semibold ${
                isDashboardRoute
                  ? 'bg-white text-txt-primary shadow-sm'
                  : 'text-txt-secondary'
              }`}
            >
              <Squares2X2Icon className="size-3.5" />
              Dashboard
            </button>
            <button
              type="button"
              onClick={() => navigate('/table')}
              className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 text-sm font-semibold ${
                !isDashboardRoute
                  ? 'bg-white text-txt-primary shadow-sm'
                  : 'text-txt-secondary'
              }`}
            >
              <TableCellsIcon className="size-3.5" />
              Table
            </button>
          </div>
        </div>

        <TooltipProvider>
          <div className="flex items-center gap-2 text-[hsl(var(--fill-gray-high))]">
            <FeatureGate feature="to-do" mode="wrap">
              <Link to="/my-todos">
                <Button variant="outline" className="hidden desktop:flex">
                  <QueueListIcon />
                  <ButtonText>My Todos</ButtonText>
                  <div className="flex min-w-5 items-center justify-center rounded-full bg-accent-tr-24 px-1.5 py-[0.125rem] text-xs text-txt-accent">
                    <span>{todoCount ?? 0}</span>
                  </div>
                </Button>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      iconOnly
                      className="relative flex desktop:hidden"
                    >
                      <QueueListIcon />
                      <div className="absolute -right-1 -top-1 flex min-w-4 items-center justify-center rounded-full bg-accent-tr-24 px-1 text-[10px] text-txt-accent">
                        {todoCount ?? 0}
                      </div>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>My Todos</TooltipContent>
                </Tooltip>
              </Link>
            </FeatureGate>

            <FeatureGate feature="money-paths" mode="wrap">
              <Link to="/money-paths">
                <Button variant="outline" className="hidden desktop:flex">
                  <ArrowPathRoundedSquareIcon />
                  <ButtonText>Money Paths</ButtonText>
                </Button>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      iconOnly
                      className="flex desktop:hidden"
                    >
                      <ArrowPathRoundedSquareIcon />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Money Paths</TooltipContent>
                </Tooltip>
              </Link>
            </FeatureGate>

            <FeatureGate feature="models" mode="wrap">
              <Button
                variant="outline"
                className="hidden desktop:flex"
                onClick={() => navigate('/models')}
              >
                <BeakerIcon />
                <ButtonText>Models</ButtonText>
                <div className="flex size-5 items-center justify-center rounded-full bg-accent-tr-24 text-xs text-txt-accent">
                  <span>{enabledModelsCount}</span>
                </div>
              </Button>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    iconOnly
                    className="relative flex desktop:hidden"
                    onClick={() => navigate('/models')}
                  >
                    <BeakerIcon />
                    <div className="absolute -right-1 -top-1 flex min-w-4 items-center justify-center rounded-full bg-accent-tr-24 px-1 text-[10px] text-txt-accent">
                      {enabledModelsCount}
                    </div>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Models ({enabledModelsCount} enabled)</TooltipContent>
              </Tooltip>
            </FeatureGate>

            <HorizonMenu />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button>
                  <PlusCircleIcon />
                  <ButtonText className="hidden desktop:inline">
                    Add Transaction
                  </ButtonText>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => navigate('/transactions/new?type=in')}
                  >
                    <ArrowUpRightIcon />
                    <ButtonText>Money In</ButtonText>
                  </Button>
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => navigate('/transactions/new?type=out')}
                  >
                    <ArrowDownRightIcon />
                    <ButtonText>Money Out</ButtonText>
                  </Button>
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() =>
                      navigate('/transactions/new?type=transfer')
                    }
                  >
                    <ArrowPathRoundedSquareIcon />
                    <ButtonText>Transfer</ButtonText>
                  </Button>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </TooltipProvider>
      </div>

      {/* ROW 2 — view-specific toolbar */}
      <div className="flex items-center gap-2 overflow-x-auto border-b border-primary bg-layout-secondary px-3 py-2 pf-hscroll">
        {isDashboardRoute ? <DashboardControlRow /> : <TableControlRow />}
      </div>
    </div>
  )
}
